import { useState, useMemo } from 'react'
import { IAPISimulationSuite } from 'extra/types/ApiModels'
import { api } from '../../api/client'
import { useEntities } from '../../context/EntitiesContext'

export function SimulationSettings ({
  suite,
}: {
  suite: IAPISimulationSuite
}) {
  const [name, setName] = useState(suite.name)
  const [promptVariantIds, setPromptVariantIds] = useState<Array<string>>(suite.promptVariantIds || [])
  const [deploymentIds, setDeploymentIds] = useState<Array<string>>(suite.deploymentIds || [])
  const [runsPerItem, setRunsPerItem] = useState(suite.runsPerItem)
  const [maxTurns, setMaxTurns] = useState(suite.maxTurns)

  const { variants: allVariants, deployments: allDeployments, refreshSimulationSuites } = useEntities()
  const availableVariants = useMemo(() => allVariants.filter(v => v.promptId === suite.promptId), [allVariants, suite.promptId])
  const availableDeployments = useMemo(() => allDeployments.filter(d => d.promptId === suite.promptId), [allDeployments, suite.promptId])

  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const autosave = async (fields: Record<string, any>) => {
    try {
      await api.simulations.update(suite.id, fields)
      await refreshSimulationSuites()
    } catch (e) {
      console.error('Failed to autosave', e)
    }
  }

  const handleNameBlur = () => {
    if (name !== suite.name) {
      autosave({ name })
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete simulation suite "${suite.name}"? This cannot be undone.`)) return
    setDeleting(true)
    try {
      await api.simulations.delete(suite.id)
      await refreshSimulationSuites()
    } catch {
      setError('Failed to delete simulation suite')
      setDeleting(false)
    }
  }

  const toggleAndSave = (id: string, current: Array<string>, setter: (val: Array<string>) => void, field: string) => {
    const newValue = current.includes(id)
      ? current.filter(i => i !== id)
      : [...current, id]
    setter(newValue)
    autosave({ [field]: newValue })
  }

  const handleRunsPerItemBlur = () => {
    if (runsPerItem !== suite.runsPerItem) {
      autosave({ runsPerItem })
    }
  }

  const handleMaxTurnsBlur = () => {
    if (maxTurns !== suite.maxTurns) {
      autosave({ maxTurns })
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      {error && <div className="alert-error">{error}</div>}

      <div>
        <label className="input-label">Name</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          onBlur={handleNameBlur}
          className="input"
        />
      </div>

      <div className="flex gap-4">
        <div className="flex-1">
          <label className="input-label">Runs per Item</label>
          <input
            type="number"
            value={runsPerItem}
            onChange={e => setRunsPerItem(parseInt(e.target.value) || 1)}
            onBlur={handleRunsPerItemBlur}
            className="input"
            min={1}
            max={20}
          />
        </div>
        <div className="flex-1">
          <label className="input-label">Max Turns</label>
          <input
            type="number"
            value={maxTurns}
            onChange={e => setMaxTurns(parseInt(e.target.value) || 1)}
            onBlur={handleMaxTurnsBlur}
            className="input"
            min={1}
            max={100}
          />
        </div>
      </div>

      <div>
        <label className="input-label">Prompt Variants</label>
        <div className="max-h-48 overflow-y-auto border border-gray-700 rounded p-2 bg-gray-900">
          {availableVariants.map(v => (
            <div key={v.id} className="flex items-center gap-2 py-1">
              <input
                type="checkbox"
                checked={promptVariantIds.includes(v.id)}
                onChange={() => toggleAndSave(v.id, promptVariantIds, setPromptVariantIds, 'promptVariantIds')}
              />
              <span className="text-sm">{v.name}</span>
            </div>
          ))}
          {availableVariants.length === 0 && <div className="text-gray-500 text-sm">No variants found.</div>}
        </div>
      </div>

      <div>
        <label className="input-label">Deployments</label>
        <div className="max-h-48 overflow-y-auto border border-gray-700 rounded p-2 bg-gray-900">
          {availableDeployments.map(d => (
            <div key={d.id} className="flex items-center gap-2 py-1">
              <input
                type="checkbox"
                checked={deploymentIds.includes(d.id)}
                onChange={() => toggleAndSave(d.id, deploymentIds, setDeploymentIds, 'deploymentIds')}
              />
              <span className="text-sm">{d.label || d.id}</span>
            </div>
          ))}
          {availableDeployments.length === 0 && <div className="text-gray-500 text-sm">No deployments found.</div>}
        </div>
      </div>

      <div className="flex justify-end pt-4">
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="btn btn-danger-light"
        >
          Delete Suite
        </button>
      </div>
    </div>
  )
}
